interface Props {
  label: string;
  sources: string[];
  /** Шаблон трейлинг-элемента: «и ещё {count} площадок». */
  moreLabel: string;
  /** Приписка в конце строки («· обновление каждые несколько часов»). */
  updateNote: string;
  /**
   * Живое число включённых источников (`data.sourceCount`) — `undefined`,
   * пока лендинг ещё не получил статистику (пустая пререндер-оболочка).
   * `remaining = sourceCount - sources.length` считается здесь, а не в
   * контенте: N обязан следовать за РЕАЛЬНЫМ набором источников, а не за
   * захардкоженным числом, которое уже разошлось с продом (см. CLAUDE.md
   * про `cooperation`, отмеченный «отключён» при живом источнике).
   */
  sourceCount?: number;
}

/**
 * Полоса «Данные из: …» под первым экраном — одна и та же на обоих лендингах.
 *
 * Отступление 3/11 (ревью владельца, 2026-08-18): раньше телефон получал
 * ОДНУ строку с горизонтальной прокруткой — и большинство площадок оставались
 * за кадром, посетитель их никогда не видел. Это било прямо по смыслу лейбла
 * («Если тендера нет у нас — его нет нигде») — недоказуемое утверждение,
 * если сам список не виден.
 *
 * ⚠️ Первая версия этого отступления держала перенос ТОЛЬКО на мобильном
 * (`sm:flex-nowrap sm:overflow-x-auto`), а на десктопе — принудительно одну
 * строку со скроллом. QA на 1440px поймал классический баг «центрированного
 * переполнения»: `/ip`-список (короче) укладывался, а `/start`-список
 * (восемь площадок + МФИ-название) шире 1120px, и с `justify-center` +
 * `overflow-x-auto` + скрытым скроллбаром содержимое обрезалось с ОБОИХ
 * краёв и было НЕДОСТИЖИМО прокруткой — `scrollLeft` центрированного
 * flex-контейнера не долистывает до реально левого края переполнения. Фикс:
 * `flex-wrap` теперь БЕЗ breakpoint-исключения — единое поведение везде.
 * Строка, которая помещается в 1120px (как у `/ip`), остаётся одной строкой
 * сама по себе (`flex-wrap` не переносит то, что и так влезло); та, что не
 * помещается (как у `/start`), переносится на несколько центрированных
 * строк — то же самое, что уже работало на мобильном, но без специального
 * случая для десктопа. Обрезки и недостижимого скролла в такой раскладке не
 * бывает вообще: `flex-wrap` не обрезает, а переносит.
 */
export function SourcesStrip({ label, sources, moreLabel, updateNote, sourceCount }: Props) {
  // Данные-управляемое опущение, не CSS-скрытие: без живого sourceCount
  // (пустая пререндер-оболочка) трейлинг просто не рендерится, а не прячется.
  const remaining = sourceCount != null ? Math.max(0, sourceCount - sources.length) : 0;

  return (
    <div className="border-y border-edge bg-bone-surface py-3.5">
      <div className="mx-auto flex max-w-[1120px] flex-wrap items-center justify-center gap-x-[22px] gap-y-2 px-5 text-[13px] font-bold text-ink-soft">
        <b className="whitespace-nowrap font-extrabold text-ink-muted">{label}</b>
        {sources.map((s) => (
          <span key={s} className="whitespace-nowrap">
            {s}
          </span>
        ))}
        {remaining > 0 && (
          <span className="whitespace-nowrap font-extrabold text-brand">
            {moreLabel.replace('{count}', String(remaining))}
          </span>
        )}
        <span className="whitespace-nowrap">{updateNote}</span>
      </div>
    </div>
  );
}
